import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postText, ApiError } from '@/shared/api/api-client';
import type { BlockSlotRequest } from '@/types/block-availability-api';
import { APPOINTMENT_LIST_QUERIES } from './appointment-list-keys';

/**
 * POST /api/Scheduling/BlockSlot
 *
 * Wire shape verified via dev curl on 2026-05-18 — matches the
 * Dart `BlockAvailability.toJson()` exactly (6 keys: actorId, start,
 * end, comment, practiceId, recurring).
 *
 * `accept: text/plain` per the live curl. The response IS JSON but
 * ships with a text/plain content-type, so we POST → parse text → JSON.parse.
 * Mirrors Flutter `scheduling_data_source_impl.dart:485-486`:
 *   `return updateSlotStatusFromJson(utf8.decode(response.data));`
 *
 * Date contract: `start` / `end` are NO-Z local-time ISO
 * (`YYYY-MM-DDTHH:mm:ss.sss`). The .NET model binder rejects the
 * `Z` suffix on these fields. Callers MUST strip Z OR build the
 * string from local components — never `new Date().toISOString()`.
 */

/** Conflict row in the BlockSlot response — surfaces existing appointments
 *  in the requested slot so the UI can warn the user. Per
 *  Flutter `update_slots_status_model.dart:31-72` (`Blocker`). */
export interface BlockSlotConflict {
  id: string | null;
  /** Conflict start time — comes back as ISO. */
  start: string | null;
  /** Conflict end time — ISO. */
  end: string | null;
  status: { code: string | null; display: string | null; definition: string | null } | null;
  appointmentType: { code: string | null; display: string | null; definition: string | null } | null;
  location: {
    id: string | null;
    name: string | null;
    description: string | null;
    isArchived: boolean | null;
    imageUrl: string | null;
  } | null;
  reasonCode: { code: string | null; display: string | null; definition: string | null } | null;
  patient: { id: string | null; name: string | null; imageUrl: string | null } | null;
}

/**
 * Response from `POST /api/Scheduling/BlockSlot`. Flutter
 * `UpdateSlotStatus` (`update_slots_status_model.dart:11-29`).
 *
 * `status` is the textual outcome (typically `"Success"`).
 * `blockers` is populated only when there are conflicting
 * appointments — server-side it's `null` on a clean block.
 */
export interface BlockSlotResponse {
  status: string | null;
  blockers: BlockSlotConflict[] | null;
}

/**
 * The one block POST both routes run — the client's `BlockSlotCore`
 * (PROD-2510).
 *
 * The device route was added by mirroring, not branching (Andrew, 2026-09-22:
 * "lets mirror better because if later they became different"), and the
 * backend mirrored by DERIVING: `BlockResourceSlotCommand : BlockSlotCommand`,
 * one handler, one implementation, two thin endpoints. This is the same shape
 * on this side — the ROUTE is the only thing the two hooks disagree about, and
 * the response contract (including the bare-`"Success"` quirk below) is stated
 * once so the two cannot drift into reading the same body differently.
 *
 * A plain function rather than a shared hook on purpose: there is nothing
 * stateful to share, and an exported hook nobody should call directly is a
 * footgun sitting next to the two that should be.
 */
export async function postBlockSlotTo(
  route: string,
  body: BlockSlotRequest,
): Promise<BlockSlotResponse> {
  const text = await postText(route, body);
  if (!text) return { status: null, blockers: null };
  try {
    return JSON.parse(text) as BlockSlotResponse;
  } catch {
    // Backend occasionally returns a bare `"Success"` string. Wrap it.
    return { status: text, blockers: null };
  }
}

/**
 * Block changes invalidate the calendar's appointment lists — the row needs to
 * render in the grid right away. Mirrors Flutter's
 * `SaveBlockAvailabilitySuccessState` triggering `AppointmentsListEvent`.
 *
 * Shared by both routes because BOTH lists have to move: `APPOINTMENT_LIST_QUERIES`
 * covers the practitioner and device roots alike, and a device block that
 * refreshed only one of them would leave the other tab showing a window the
 * server has already marked busy.
 */
export function invalidateAfterBlockChange(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries(APPOINTMENT_LIST_QUERIES);
  void qc.invalidateQueries({ queryKey: ['day-list-appointments'] });
  void qc.invalidateQueries({ queryKey: ['month-appointments'] });
}

/** The PRACTITIONER block route. A device sent here is refused 400 — see below. */
export function useSaveBlockSlot() {
  const qc = useQueryClient();
  return useMutation<BlockSlotResponse, ApiError, BlockSlotRequest>({
    mutationKey: ['save-block-slot'],
    mutationFn: (body) => postBlockSlotTo('/api/Scheduling/BlockSlot', body),
    onSuccess: () => invalidateAfterBlockChange(qc),
  });
}
